/**
 * API Client for Filmssavn Admin
 * Uses the REAL Flowcore chunked upload workflow + Keycloak auth
 */

import { getToken } from './keycloak.js';

// Auto-detect API URL based on current host
// Uses relative URL if VITE_API_URL is not set, making it work from any domain/IP
const getApiUrl = () => {
  const envUrl = import.meta.env.VITE_API_URL;
  if (envUrl) return envUrl;

  // Use relative URL - Vite proxy will handle /api requests
  return '/api';
};

const API_URL = getApiUrl();
const API_KEY = import.meta.env.VITE_API_KEY || 'dev-key-12345';
const FLOWCORE_WEBHOOK_URL = import.meta.env.VITE_FLOWCORE_WEBHOOK_URL;

class AdminApiClient {
  constructor() {
    this.baseUrl = API_URL;
    this.apiKey = API_KEY;
    this.flowcoreWebhookUrl = FLOWCORE_WEBHOOK_URL;
  }

  /**
   * Resolve an asset URL returned by the API into one the browser can load.
   * The API stores image URLs as path-relative `/images/asset/{id}` strings;
   * those resolve against the page origin (admin.stroyma.fo) which has no
   * such route. Prepend the API base so the request hits the API instead.
   * URLs that are already absolute (http(s)://, data:, blob:) pass through.
   */
  resolveAsset(url) {
    if (!url) return null;
    if (/^(https?:|data:|blob:)/i.test(url)) return url;
    return `${this.baseUrl}${url}`;
  }

  /**
   * Get auth headers (Keycloak token or fallback to API key)
   */
  async getAuthHeaders() {
    const token = await getToken();
    if (token) {
      return { Authorization: `Bearer ${token}` };
    }
    // Fallback to API key for backward compatibility
    return { Authorization: `Bearer ${this.apiKey}` };
  }

  /**
   * Save upload progress to localStorage for resumable uploads
   * @param {string} videoId - Video ID
   * @param {string} fileName - File name
   * @param {number} fileSize - File size in bytes
   * @param {number} totalChunks - Total number of chunks
   * @param {number[]} uploadedChunks - Array of uploaded chunk indices
   * @param {string[]} blockIds - Array of block IDs (base64)
   */
  saveUploadProgress(videoId, fileName, fileSize, totalChunks, uploadedChunks, blockIds) {
    const progressKey = `upload_${videoId}`;
    const progressData = {
      videoId,
      fileName,
      fileSize,
      totalChunks,
      uploadedChunks,
      blockIds,
      lastUpdated: Date.now(),
    };

    try {
      localStorage.setItem(progressKey, JSON.stringify(progressData));
      console.log(`💾 Progress saved: ${uploadedChunks.length}/${totalChunks} chunks`);
    } catch (error) {
      console.warn('⚠️ Failed to save upload progress:', error);
    }
  }

  /**
   * Get upload progress from localStorage
   * @param {string} videoId - Video ID
   * @returns {Object|null} Progress data or null if not found
   */
  getUploadProgress(videoId) {
    const progressKey = `upload_${videoId}`;

    try {
      const data = localStorage.getItem(progressKey);
      if (!data) return null;

      const progress = JSON.parse(data);
      console.log(`📂 Found saved progress: ${progress.uploadedChunks.length}/${progress.totalChunks} chunks`);
      return progress;
    } catch (error) {
      console.warn('⚠️ Failed to load upload progress:', error);
      return null;
    }
  }

  /**
   * Clear upload progress from localStorage
   * @param {string} videoId - Video ID
   */
  clearUploadProgress(videoId) {
    const progressKey = `upload_${videoId}`;

    try {
      localStorage.removeItem(progressKey);
      console.log(`🗑️ Progress cleared for ${videoId}`);
    } catch (error) {
      console.warn('⚠️ Failed to clear upload progress:', error);
    }
  }

  /**
   * Get all incomplete uploads from localStorage
   * @returns {Array} Array of progress objects
   */
  getAllIncompleteUploads() {
    const incompleteUploads = [];

    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith('upload_')) {
          const data = localStorage.getItem(key);
          if (data) {
            const progress = JSON.parse(data);
            // Only include if not 100% complete
            if (progress.uploadedChunks.length < progress.totalChunks) {
              incompleteUploads.push(progress);
            } else {
              // Clean up completed uploads that weren't cleared
              localStorage.removeItem(key);
            }
          }
        }
      }
    } catch (error) {
      console.warn('⚠️ Failed to get incomplete uploads:', error);
    }

    return incompleteUploads;
  }

  /**
   * Get all videos from database (via API)
   * Uses role-based endpoint: filmmakers see only their videos
   */
  async getVideos() {
    const response = await fetch(`${this.baseUrl}/filmmaker/videos`, {
      headers: await this.getAuthHeaders(),
    });

    if (!response.ok) {
      // Fallback to old endpoint if new one fails
      const fallbackResponse = await fetch(`${this.baseUrl}/videos/admin`, {
        headers: await this.getAuthHeaders(),
      });

      if (!fallbackResponse.ok) {
        throw new Error(`Failed to fetch videos: ${fallbackResponse.statusText}`);
      }

      const { videos } = await fallbackResponse.json();
      return videos || [];
    }

    const { videos } = await response.json();
    return videos || [];
  }

  /**
   * Get rental and free-film statistics for the admin dashboard
   */
  async getRentalStats(year = null) {
    const params = year ? `?year=${encodeURIComponent(year)}` : '';
    const response = await fetch(`${this.baseUrl}/admin/stats/rentals${params}`, {
      headers: await this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch rental stats: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Get application users and app-controlled roles
   */
  async getUsers() {
    const response = await fetch(`${this.baseUrl}/admin/users`, {
      headers: await this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch users: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Update a user's application role
   */
  async updateUserRole(userId, role) {
    const response = await fetch(`${this.baseUrl}/admin/users/${encodeURIComponent(userId)}/role`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...(await this.getAuthHeaders()),
      },
      body: JSON.stringify({ role }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || error.error || `Failed to update user role: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Upload video directly to Azure Blob Storage:
   * 1. POST /videos (create video and get SAS upload URL)
   * 2. PUT file directly to Azure using SAS URL
   * 3. POST /videos/:id/finalize (trigger encoding)
   */
  async uploadVideo(file, metadata = {}, onProgress = null) {
    // Step 1: Create video in API to get videoId and uploadUrl
    const createResponse = await fetch(`${this.baseUrl}/videos`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(await this.getAuthHeaders()),
      },
      body: JSON.stringify({
        fileName: file.name,
        fileSize: file.size,
        mimeType: file.type,
        metadata: metadata,
      }),
    });

    if (!createResponse.ok) {
      throw new Error(`Failed to create video: ${createResponse.statusText}`);
    }

    const createData = await createResponse.json();
    const { videoId } = createData;

    // Step 2: Upload file — Assets Platform chunked or legacy Azure
    if (createData.uploadType === 'assets-chunked') {
      // Assets Platform: upload chunks directly to Assets API
      const { sessionId, chunkSize, totalChunks, chunkUploadUrlBase } = createData;
      console.log(`Assets chunked upload: ${totalChunks} chunks of ${chunkSize} bytes`);

      for (let i = 0; i < totalChunks; i++) {
        const start = i * chunkSize;
        const end = Math.min(start + chunkSize, file.size);
        const chunk = file.slice(start, end);

        const chunkUrl = `${this.baseUrl}${chunkUploadUrlBase}/${i}`;
        const response = await fetch(chunkUrl, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/octet-stream',
            ...(await this.getAuthHeaders()),
          },
          body: chunk,
        });

        if (!response.ok) {
          throw new Error(`Failed to upload chunk ${i}: ${response.statusText}`);
        }

        if (onProgress) {
          onProgress(Math.round(((i + 1) / totalChunks) * 100));
        }
      }

      // Step 3: Sync with API (triggers encoding completion on Assets)
      const syncResponse = await fetch(`${this.baseUrl}/videos/sync`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(await this.getAuthHeaders()),
        },
        body: JSON.stringify({
          videoId,
          fileName: file.name,
          state: 'uploaded',
          published: false,
          uploadedAt: new Date().toISOString(),
          assetsVideoId: createData.assetsVideoId,
          assetsSessionId: sessionId,
          ...metadata,
        }),
      });

      if (!syncResponse.ok) {
        const errorText = await syncResponse.text();
        throw new Error(`Upload succeeded but sync failed: ${syncResponse.status} - ${errorText}`);
      }

      console.log('Video uploaded via Assets Platform:', videoId);
      return { videoId, fileName: file.name, ...metadata };
    }

    // Legacy Azure flow
    const { uploadUrl } = createData;
    const CHUNK_SIZE = 4 * 1024 * 1024; // 4 MB chunks
    const MAX_SINGLE_UPLOAD_SIZE = 256 * 1024 * 1024; // 256 MB - Azure's limit for single PUT

    if (file.size <= MAX_SINGLE_UPLOAD_SIZE) {
      // Small file - use simple PUT
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable && onProgress) {
            const percentComplete = Math.round((e.loaded / e.total) * 100);
            onProgress(percentComplete);
          }
        });

        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            if (onProgress) onProgress(100);
            resolve();
          } else {
            reject(new Error(`Failed to upload video: ${xhr.statusText}`));
          }
        });

        xhr.addEventListener('error', () => reject(new Error('Network error during upload')));
        xhr.addEventListener('abort', () => reject(new Error('Upload aborted')));

        xhr.open('PUT', uploadUrl);
        xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');
        xhr.setRequestHeader('Content-Type', file.type || 'video/mp4');
        xhr.send(file);
      });
    } else {
      // Large file - use chunked upload with retry logic (Put Block + Put Block List)
      const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

      // Check for existing upload progress (resumable upload)
      const savedProgress = this.getUploadProgress(videoId);
      const uploadedChunks = savedProgress?.uploadedChunks || [];
      const blockIds = savedProgress?.blockIds || [];

      // Verify saved progress matches current file
      if (savedProgress && (savedProgress.fileSize !== file.size || savedProgress.totalChunks !== totalChunks)) {
        console.warn(`⚠️ Saved progress doesn't match current file, starting fresh`);
        uploadedChunks.length = 0;
        blockIds.length = 0;
        this.clearUploadProgress(videoId);
      }

      const isResuming = uploadedChunks.length > 0;
      if (isResuming) {
        console.log(`🔄 Resuming upload from chunk ${uploadedChunks.length}/${totalChunks}`);
      } else {
        console.log(`📦 Uploading ${totalChunks} chunks for ${videoId}...`);
      }

      // Start keepalive to prevent session timeout during long upload
      const stopKeepalive = this.startKeepalive(videoId);

      try {
        for (let i = 0; i < totalChunks; i++) {
          // Skip already uploaded chunks (resumable upload)
          if (uploadedChunks.includes(i)) {
            console.log(`⏭️ Skipping chunk ${i + 1}/${totalChunks} (already uploaded)`);

            // Update progress UI
            if (onProgress) {
              const progress = Math.round(((i + 1) / totalChunks) * 100);
              onProgress(progress);
            }
            continue;
          }

          const start = i * CHUNK_SIZE;
          const end = Math.min(start + CHUNK_SIZE, file.size);
          const chunk = file.slice(start, end);

          // Generate or reuse block ID (must be base64 and same length for all blocks)
          const blockId = blockIds[i] || btoa(String(i).padStart(6, '0'));
          if (!blockIds[i]) blockIds[i] = blockId;

          // Build chunk URL
          const blockUrl = `${uploadUrl + (uploadUrl.includes('?') ? '&' : '?')}comp=block&blockid=${encodeURIComponent(blockId)}`;

          // Upload block with retry logic and auto token refresh
          await this.uploadChunkWithRetry(chunk, blockUrl, videoId, i);

          // Mark chunk as uploaded and save progress
          uploadedChunks.push(i);
          this.saveUploadProgress(videoId, file.name, file.size, totalChunks, uploadedChunks, blockIds);

          // Update progress UI
          if (onProgress) {
            const progress = Math.round(((i + 1) / totalChunks) * 100);
            onProgress(progress);
          }
        }

        console.log(`✅ All chunks uploaded, committing blocks...`);

        // Commit all blocks
        await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();

          xhr.addEventListener('load', () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              if (onProgress) onProgress(100);
              resolve();
            } else {
              reject(new Error(`Failed to commit blocks: ${xhr.statusText}`));
            }
          });

          xhr.addEventListener('error', () => reject(new Error('Network error committing blocks')));
          xhr.addEventListener('abort', () => reject(new Error('Upload aborted')));

          // Build block list XML
          const blockListXml =
            '<?xml version="1.0" encoding="utf-8"?><BlockList>' +
            blockIds.map((id) => `<Latest>${id}</Latest>`).join('') +
            '</BlockList>';

          const commitUrl = `${uploadUrl + (uploadUrl.includes('?') ? '&' : '?')}comp=blocklist`;
          xhr.open('PUT', commitUrl);
          xhr.setRequestHeader('Content-Type', 'application/xml');
          xhr.send(blockListXml);
        });

        // Clear progress after successful upload
        this.clearUploadProgress(videoId);
      } finally {
        // Always stop keepalive when upload completes or fails
        stopKeepalive();
      }
    }

    // Step 3: Register video in database with metadata
    // Note: Get fresh auth headers in case token expired during long upload
    const syncResponse = await fetch(`${this.baseUrl}/videos/sync`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(await this.getAuthHeaders()),
      },
      body: JSON.stringify({
        videoId,
        fileName: file.name,
        state: 'uploaded',
        published: false,
        uploadedAt: new Date().toISOString(),
        error: null, // Clear any previous error
        ...metadata,
      }),
    });

    if (!syncResponse.ok) {
      const errorText = await syncResponse.text();
      console.error('Failed to register video in database:', errorText);

      // Special handling for session expiration during long uploads
      if (syncResponse.status === 401) {
        throw new Error(
          `✅ Video uploaded successfully!\n\n` +
            `❌ However, your session expired during the upload.\n\n` +
            `➡️ Please refresh the page, find your video in the list, and click "🔄 Retry Encoding" to start encoding.`,
        );
      }

      throw new Error(`Upload succeeded but metadata sync failed: ${syncResponse.status} - ${errorText}`);
    }

    console.log('Video uploaded and synced successfully:', videoId);
    return { videoId, fileName: file.name, ...metadata };
  }

  /**
   * Keepalive mechanism - pings API to refresh token and keep session alive
   * @param {string} videoId - Video ID (for context in logs)
   * @returns {Function} Stop function to clear the interval
   */
  startKeepalive(videoId) {
    console.log(`💓 Starting keepalive for ${videoId} (pings every 4 minutes)`);

    const intervalId = setInterval(
      async () => {
        try {
          // Simple ping to keep session alive and refresh token
          const response = await fetch(`${this.baseUrl}/videos/${videoId}/status`, {
            method: 'GET',
            headers: await this.getAuthHeaders(),
          });

          if (response.ok) {
            console.log(`💓 Keepalive ping successful (${new Date().toLocaleTimeString()})`);
          } else {
            console.warn(`⚠️ Keepalive ping failed: ${response.status}`);
          }
        } catch (error) {
          console.warn(`⚠️ Keepalive error:`, error.message);
        }
      },
      4 * 60 * 1000,
    ); // Every 4 minutes

    // Return stop function
    return () => {
      clearInterval(intervalId);
      console.log(`💓 Keepalive stopped for ${videoId}`);
    };
  }

  /**
   * Refresh upload URL (get new SAS token)
   * @param {string} videoId - Video ID
   */
  async refreshUploadUrl(videoId) {
    const response = await fetch(`${this.baseUrl}/videos/${videoId}/refresh-upload-url`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(await this.getAuthHeaders()),
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to refresh upload URL: ${response.statusText}`);
    }

    const { uploadUrl } = await response.json();
    console.log(`🔄 Upload URL refreshed for ${videoId}`);
    return uploadUrl;
  }

  /**
   * Upload a chunk with retry logic and token refresh
   * @param {Blob} chunk - Chunk data
   * @param {string} uploadUrl - Current upload URL
   * @param {string} videoId - Video ID (for token refresh)
   * @param {number} chunkIndex - Chunk index
   * @param {number} maxRetries - Maximum retry attempts
   */
  async uploadChunkWithRetry(chunk, uploadUrl, videoId, chunkIndex, maxRetries = 3) {
    let currentUrl = uploadUrl;

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();

          xhr.addEventListener('load', () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              resolve();
            } else if (xhr.status === 403 || xhr.status === 401) {
              // SAS token expired - reject with special error
              reject(new Error('TOKEN_EXPIRED'));
            } else {
              reject(new Error(`HTTP ${xhr.status}: ${xhr.statusText}`));
            }
          });

          xhr.addEventListener('error', () => reject(new Error('Network error')));
          xhr.addEventListener('abort', () => reject(new Error('Upload aborted')));

          xhr.open('PUT', currentUrl);
          xhr.setRequestHeader('Content-Type', chunk.type || 'video/mp4');
          xhr.send(chunk);
        });

        // Success! Return
        return;
      } catch (error) {
        console.warn(`⚠️ Chunk ${chunkIndex} attempt ${attempt + 1} failed:`, error.message);

        // If token expired, refresh and retry
        if (error.message === 'TOKEN_EXPIRED' && attempt < maxRetries - 1) {
          console.log(`🔄 Token expired, refreshing URL...`);
          const newUploadUrl = await this.refreshUploadUrl(videoId);
          // Update URL for this chunk
          currentUrl = currentUrl.replace(/^[^?]+/, newUploadUrl.split('?')[0]);
          continue;
        }

        // If network error and retries left, wait and retry
        if (error.message.includes('Network') && attempt < maxRetries - 1) {
          const waitTime = Math.min(1000 * 2 ** attempt, 10000); // Exponential backoff, max 10s
          console.log(`⏳ Waiting ${waitTime}ms before retry...`);
          await new Promise((resolve) => setTimeout(resolve, waitTime));
          continue;
        }

        // Last attempt or non-retriable error
        if (attempt === maxRetries - 1) {
          throw new Error(`Failed to upload chunk ${chunkIndex} after ${maxRetries} attempts: ${error.message}`);
        }
      }
    }
  }

  /**
   * Replace existing video file (for re-upload/re-encoding)
   * @param {File} file - New video file
   * @param {string} videoId - Existing video ID to replace
   * @param {Function} onProgress - Progress callback (0-100)
   */
  async replaceVideo(file, videoId, onProgress = null) {
    // Step 1: Init a replacement Assets chunked upload session.
    const initResponse = await fetch(`${this.baseUrl}/videos/${videoId}/replace-upload-url`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(await this.getAuthHeaders()),
      },
      body: JSON.stringify({
        fileName: file.name,
        fileSize: file.size,
        mimeType: file.type || 'video/mp4',
      }),
    });

    if (!initResponse.ok) {
      const errorText = await initResponse.text().catch(() => '');
      throw new Error(`Failed to init replacement upload: ${initResponse.status} ${errorText}`);
    }

    const initData = await initResponse.json();
    if (initData.uploadType !== 'assets-chunked') {
      throw new Error(`Unexpected replace-upload-url response: ${JSON.stringify(initData)}`);
    }

    const { sessionId, chunkSize, totalChunks, chunkUploadUrlBase, assetsVideoId } = initData;
    console.log(`Assets replacement chunked upload: ${totalChunks} chunks of ${chunkSize} bytes`);

    // Step 2: Stream chunks through our API proxy (same path as fresh uploads).
    for (let i = 0; i < totalChunks; i++) {
      const start = i * chunkSize;
      const end = Math.min(start + chunkSize, file.size);
      const chunk = file.slice(start, end);

      const chunkUrl = `${this.baseUrl}${chunkUploadUrlBase}/${i}`;
      const response = await fetch(chunkUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/octet-stream',
          ...(await this.getAuthHeaders()),
        },
        body: chunk,
      });

      if (!response.ok) {
        throw new Error(`Failed to upload chunk ${i}: ${response.status} ${response.statusText}`);
      }

      if (onProgress) {
        onProgress(Math.round(((i + 1) / totalChunks) * 100));
      }
    }

    // Step 3: Complete the session — triggers encoding on Assets.
    const syncResponse = await fetch(`${this.baseUrl}/videos/sync`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(await this.getAuthHeaders()),
      },
      body: JSON.stringify({
        videoId,
        fileName: file.name,
        state: 'uploaded',
        uploadedAt: new Date().toISOString(),
        assetsVideoId,
        assetsSessionId: sessionId,
        error: null,
      }),
    });

    if (!syncResponse.ok) {
      const errorText = await syncResponse.text().catch(() => '');
      throw new Error(`Upload succeeded but sync failed: ${syncResponse.status} - ${errorText}`);
    }

    console.log('Video replaced successfully, encoding triggered:', videoId);
    return { videoId, fileName: file.name, assetsVideoId };
  }

  /**
   * Helper: Read file as ArrayBuffer
   */
  readFileAsArrayBuffer(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(reader.error);
      reader.readAsArrayBuffer(file);
    });
  }

  /**
   * Helper: Calculate SHA-256 checksum
   */
  async calculateSHA256(arrayBuffer) {
    const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
  }

  /**
   * Helper: Convert ArrayBuffer to Base64
   */
  arrayBufferToBase64(buffer) {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  /**
   * Get video status
   */
  async getVideoStatus(videoId) {
    console.log(`📊 Fetching status for video: ${videoId}`);
    const response = await fetch(`${this.baseUrl}/videos/${videoId}/status`);

    if (!response.ok) {
      console.error(`❌ Failed to get status for ${videoId}: ${response.statusText}`);
      throw new Error(`Failed to get status: ${response.statusText}`);
    }

    const status = await response.json();
    console.log(`📊 Status for ${videoId}:`, status);
    return status;
  }

  /**
   * Update video metadata in database
   */
  async updateVideo(videoId, metadata) {
    const response = await fetch(`${this.baseUrl}/videos/sync`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(await this.getAuthHeaders()),
      },
      body: JSON.stringify({
        videoId,
        ...metadata,
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to update video: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Delete video (soft delete — marks as deleted in database)
   */
  async deleteVideo(videoId) {
    const response = await fetch(`${this.baseUrl}/videos/${videoId}`, {
      method: 'DELETE',
      headers: await this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to delete video: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Get stream URL for player
   */
  getStreamUrl(videoId, token) {
    return `${this.baseUrl}/stream/${videoId}/manifest.m3u8?token=${token}`;
  }

  /**
   * Generate access token for streaming
   */
  async generateToken(videoId, userId = 'admin') {
    const response = await fetch(`${this.baseUrl}/videos/${videoId}/token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(await this.getAuthHeaders()),
      },
      body: JSON.stringify({
        userId,
        permissions: ['stream'],
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to generate token: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Upload image (poster or still) to Azure Blob Storage
   * @param {File} file - Image file
   * @param {string} type - 'poster' or 'still'
   * @param {string} videoId - Video ID
   * @returns {Promise<string>} - Public URL of uploaded image
   */
  async uploadImage(file, type, videoId) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);
    formData.append('videoId', videoId);

    const response = await fetch(`${this.baseUrl}/images/upload`, {
      method: 'POST',
      headers: await this.getAuthHeaders(), // Use Keycloak authentication
      body: formData,
    });

    if (!response.ok) {
      // Handle 413 Request Entity Too Large specifically
      if (response.status === 413) {
        const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
        throw new Error(`File too large (${fileSizeMB}MB). Maximum size: 50MB. Please compress or resize the image.`);
      }

      // Try to parse JSON error, fallback to status text
      let errorMessage = `Upload failed (${response.status})`;
      try {
        const error = await response.json();
        errorMessage = error.error || errorMessage;
      } catch (_e) {
        errorMessage = response.statusText || errorMessage;
      }
      throw new Error(errorMessage);
    }

    const result = await response.json();
    return { url: result.url, imageId: result.imageId || null };
  }

  /**
   * Toggle video published status in database
   * @param {string} videoId - Video ID
   * @param {boolean} published - New published status
   */
  async togglePublished(videoId, published) {
    const response = await fetch(`${this.baseUrl}/videos/${videoId}/publish`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(await this.getAuthHeaders()),
      },
      body: JSON.stringify({ published }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Failed to toggle published: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Approve video (admin only)
   * @param {string} videoId - Video ID
   */
  async approveVideo(videoId) {
    const response = await fetch(`${this.baseUrl}/admin/videos/${videoId}/approve`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(await this.getAuthHeaders()),
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Failed to approve video: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Reject video (admin only)
   * @param {string} videoId - Video ID
   * @param {string} reason - Rejection reason
   */
  async rejectVideo(videoId, reason = '') {
    const response = await fetch(`${this.baseUrl}/admin/videos/${videoId}/reject`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(await this.getAuthHeaders()),
      },
      body: JSON.stringify({ reason }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Failed to reject video: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Retry/force re-encoding for an Assets-backed video. Calls the API's
   * /videos/:id/re-encode endpoint, which in turn hits Assets's /re-encode
   * to spawn a fresh K8s encoding Job from the existing committed blob —
   * no re-upload required. Used by both "Retry Encoding" (failed videos)
   * and "Force Re-Encode" (already-ready videos) in the admin UI.
   * @param {string} videoId - Video ID
   */
  async retryEncoding(videoId) {
    console.log(`🔄 Retrying encoding for video: ${videoId}`);

    const response = await fetch(`${this.baseUrl}/videos/${videoId}/re-encode`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(await this.getAuthHeaders()),
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error(`❌ Retry encoding failed:`, errorData);
      throw new Error(errorData.error || `Failed to retry encoding: ${response.statusText}`);
    }

    const result = await response.json();
    console.log(`✅ Retry encoding response:`, result);
    return result;
  }

  /**
   * Check for stuck videos and mark as failed (admin only)
   */
  async checkStuckVideos() {
    const response = await fetch(`${this.baseUrl}/admin/check-stuck-videos`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(await this.getAuthHeaders()),
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Failed to check stuck videos: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Upload subtitle file for a video
   * @param {string} videoId - Video ID
   * @param {File} file - Subtitle file (.srt, .vtt, .ass, .ssa)
   * @param {string} language - ISO 639 language code (e.g., 'en', 'ja', 'fo')
   * @param {string} label - Display name (e.g., 'English', '日本語', 'Føroyskt')
   */
  async uploadSubtitle(videoId, file, language, label) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('language', language);
    formData.append('label', label);

    const response = await fetch(`${this.baseUrl}/videos/${videoId}/subtitles`, {
      method: 'POST',
      headers: await this.getAuthHeaders(),
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Failed to upload subtitle: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Get all subtitles for a video
   * @param {string} videoId - Video ID
   */
  async getSubtitles(videoId) {
    const response = await fetch(`${this.baseUrl}/videos/${videoId}/subtitles`, {
      headers: await this.getAuthHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Failed to get subtitles: ${response.statusText}`);
    }

    const data = await response.json();
    return data.subtitles || [];
  }

  /**
   * Delete a subtitle
   * @param {string} subtitleId - Subtitle ID
   */
  async deleteSubtitle(subtitleId) {
    const response = await fetch(`${this.baseUrl}/subtitles/${subtitleId}`, {
      method: 'DELETE',
      headers: await this.getAuthHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Failed to delete subtitle: ${response.statusText}`);
    }

    return await response.json();
  }
}

export const api = new AdminApiClient();
